import axios, { AxiosInstance } from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://booking-i73o.onrender.com/api';
  }
  if (process.env.REACT_APP_ENV === 'qa') {
    return process.env.REACT_APP_API_URL || 'https://booking-qa.onrender.com/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
};

export const AFFILIATE_TOKEN_KEY = 'affiliateAuthToken';
export const AFFILIATE_PROFILE_KEY = 'affiliateProfile';

export interface AffiliateProfile {
  id: string;
  email: string;
  firstName: string;
  lastName?: string | null;
  isEmailVerified: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  programId: string;
  createdAt: string;
}

export interface AffiliateReferralCode {
  id: string;
  code: string;
  status: 'active' | 'paused';
  link: string;
  createdAt: string;
}

export interface AffiliateReferralRow {
  id: string;
  businessName: string;
  registeredAt: string;
  code: string;
  active: boolean;
  stage: string;
  trialBonusStatus: string;
  trialBonusCents: number;
  commissionCents: number;
  payoutStatus: string;
  subscriptionMonth: number | null;
}

export interface AffiliateDashboard {
  affiliate: AffiliateProfile;
  program: {
    trialBonusCents: number;
    commissionRate: number;
    commissionMonths: number;
  };
  metrics: {
    referredBusinesses: number;
    activeBusinesses: number;
    qualifiedTrialCount: number;
    trialBonusCents: number;
    commissionCents: number;
    nextPayoutCents: number;
    payoutQuarterLabel: string;
  };
  codes: AffiliateReferralCode[];
  referrals: AffiliateReferralRow[];
}

class AffiliateApi {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem(AFFILIATE_TOKEN_KEY);
      const url = String(config.url || '');
      const isPublicAuth =
        url.includes('/affiliates/auth/login') ||
        url.includes('/affiliates/auth/register') ||
        url.includes('/affiliates/auth/google') ||
        url.includes('/affiliates/auth/verify-email') ||
        url.includes('/affiliates/auth/resend-verification');
      if (token && !isPublicAuth) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl = String(error.config?.url || '');
        const isPublicAuth = requestUrl.includes('/affiliates/auth/');
        if (error.response?.status === 401 && !isPublicAuth) {
          localStorage.removeItem(AFFILIATE_TOKEN_KEY);
          localStorage.removeItem(AFFILIATE_PROFILE_KEY);
          if (!window.location.pathname.startsWith('/affiliates')) {
            window.location.href = '/affiliates';
          } else if (!window.location.pathname.startsWith('/affiliates/verify-email') && window.location.pathname !== '/affiliates') {
            window.location.href = '/affiliates';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async register(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) {
    const response = await this.api.post('/affiliates/auth/register', payload);
    return response.data as { success: boolean; message: string };
  }

  async login(email: string, password: string) {
    const response = await this.api.post('/affiliates/auth/login', { email, password });
    return response.data.data as { token: string; affiliate: AffiliateProfile };
  }

  async googleLogin(credential: string) {
    const response = await this.api.post('/affiliates/auth/google', { credential });
    return response.data.data as { token: string; affiliate: AffiliateProfile };
  }

  async verifyEmail(token: string) {
    const response = await this.api.post('/affiliates/auth/verify-email', { token });
    return response.data as { success: boolean; message: string };
  }

  async resendVerification(email: string) {
    const response = await this.api.post('/affiliates/auth/resend-verification', { email });
    return response.data as { success: boolean; message: string };
  }

  async getDashboard() {
    const response = await this.api.get('/affiliates/dashboard');
    return response.data.data as AffiliateDashboard;
  }

  async createCode(code: string) {
    const response = await this.api.post('/affiliates/codes', { code });
    return response.data.data as AffiliateReferralCode;
  }

  async setCodeStatus(id: string, status: 'active' | 'paused') {
    const response = await this.api.patch(`/affiliates/codes/${id}`, { status });
    return response.data.data as AffiliateReferralCode;
  }
}

export const affiliateApi = new AffiliateApi();

export function saveAffiliateSession(token: string, affiliate: AffiliateProfile) {
  localStorage.setItem(AFFILIATE_TOKEN_KEY, token);
  localStorage.setItem(AFFILIATE_PROFILE_KEY, JSON.stringify(affiliate));
}

export function clearAffiliateSession() {
  localStorage.removeItem(AFFILIATE_TOKEN_KEY);
  localStorage.removeItem(AFFILIATE_PROFILE_KEY);
}

export function readAffiliateSession(): AffiliateProfile | null {
  try {
    const raw = localStorage.getItem(AFFILIATE_PROFILE_KEY);
    if (!raw || !localStorage.getItem(AFFILIATE_TOKEN_KEY)) return null;
    return JSON.parse(raw) as AffiliateProfile;
  } catch {
    return null;
  }
}
